/**
 * Detect broken / ephemeral browser storage (Safari Private, Lockdown Mode,
 * full disk, etc.) and put the app into "online-only / no-outbox" mode.
 *
 * Primary signal: real IndexedDB open/write/read probe.
 * Secondary: session flag set when Dexie throws mid-flight (ErrorBoundary / outbox).
 */

export type StorageMode = "ok" | "degraded";

const SESSION_FLAG = "storage_degraded";
const SESSION_REASON = "storage_degraded_reason";
const PROBE_DB = "__punchclock_storage_probe__";

type Listener = (mode: StorageMode, reason: string | null) => void;

let mode: StorageMode = readSessionMode();
let reason: string | null = readSessionReason();
let probePromise: Promise<StorageMode> | null = null;
const listeners = new Set<Listener>();

function readSessionMode(): StorageMode {
  try {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_FLAG) === "1") {
      return "degraded";
    }
  } catch {
    /* private / blocked sessionStorage */
  }
  return "ok";
}

function readSessionReason(): string | null {
  try {
    if (typeof sessionStorage !== "undefined") {
      return sessionStorage.getItem(SESSION_REASON);
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeSession(next: StorageMode, nextReason: string | null): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (next === "degraded") {
      sessionStorage.setItem(SESSION_FLAG, "1");
      if (nextReason) sessionStorage.setItem(SESSION_REASON, nextReason.slice(0, 300));
    }
  } catch {
    /* ignore */
  }
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn(mode, reason);
    } catch {
      /* never throw from listeners */
    }
  }
}

export function getStorageMode(): StorageMode {
  return mode;
}

export function isStorageDegraded(): boolean {
  return mode === "degraded";
}

export function getStorageDegradedReason(): string | null {
  return reason;
}

/** Subscribe to mode changes (used by StorageCapabilityProvider). */
export function subscribeStorageMode(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Force degraded mode after a runtime IDB/Dexie failure.
 * Idempotent; safe to call from ErrorBoundary / outbox catch paths.
 */
export function markStorageDegraded(why: string): void {
  const msg = (why || "storage_unavailable").slice(0, 300);
  if (mode === "degraded" && reason) {
    return;
  }
  mode = "degraded";
  reason = msg;
  writeSession(mode, reason);
  try {
    console.warn("[storage] entering degraded (online-only) mode:", msg);
  } catch {
    /* ignore */
  }
  notify();
}

export function isDexieStorageError(error: unknown): boolean {
  if (error == null) return false;
  const name = String((error as any)?.name || "");
  const msg = String((error as any)?.message || error || "");
  const lower = msg.toLowerCase();

  if (name === "QuotaExceededError" || name === "UnknownError" || name === "InvalidStateError" || name === "AbortError") {
    return true;
  }
  if (error instanceof StorageUnavailableError) return true;

  // Safari Private / Dexie optimistic-ops: null is not an object (evaluating 'e6.trans' / 't3.type')
  if (
    /null is not an object/i.test(msg) &&
    (/\.trans\b/.test(msg) || /\.type\b/.test(msg) || /evaluating/i.test(msg))
  ) {
    return true;
  }
  if (/quota|storage|space|indexed|idb|dexie|connection.*lost|database.*lost|blob\/file/i.test(lower)) {
    return true;
  }
  return false;
}

export class StorageUnavailableError extends Error {
  constructor(message = "Browser storage is unavailable (private browsing or restricted storage).") {
    super(message);
    this.name = "StorageUnavailableError";
  }
}

function idbProbe(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined" || !indexedDB) {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    let settled = false;
    const fail = (err: unknown) => {
      if (settled) return;
      settled = true;
      reject(err instanceof Error ? err : new Error(String(err)));
    };
    const ok = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    let openReq: IDBOpenDBRequest;
    try {
      openReq = indexedDB.open(PROBE_DB, 1);
    } catch (e) {
      fail(e);
      return;
    }

    openReq.onerror = () => fail(openReq.error || new Error("idb open failed"));
    openReq.onblocked = () => fail(new Error("idb open blocked"));
    openReq.onupgradeneeded = () => {
      try {
        const db = openReq.result;
        if (!db.objectStoreNames.contains("p")) {
          db.createObjectStore("p", { keyPath: "id" });
        }
      } catch (e) {
        fail(e);
      }
    };
    openReq.onsuccess = () => {
      const db = openReq.result;
      try {
        const tx = db.transaction("p", "readwrite");
        const store = tx.objectStore("p");
        store.put({ id: 1, t: Date.now() });
        const getReq = store.get(1);
        getReq.onsuccess = () => {
          try {
            db.close();
          } catch {
            /* ignore */
          }
          try {
            const del = indexedDB.deleteDatabase(PROBE_DB);
            del.onsuccess = () => ok();
            del.onerror = () => ok(); // probe write worked; cleanup best-effort
            del.onblocked = () => ok();
          } catch {
            ok();
          }
        };
        getReq.onerror = () => {
          try {
            db.close();
          } catch {
            /* ignore */
          }
          fail(getReq.error || new Error("idb get failed"));
        };
        tx.onerror = () => fail(tx.error || new Error("idb tx failed"));
        tx.onabort = () => fail(tx.error || new Error("idb tx aborted"));
      } catch (e) {
        try {
          db.close();
        } catch {
          /* ignore */
        }
        fail(e);
      }
    };
  });
}

/**
 * One-shot (cached) probe. Safe to call many times.
 * If already degraded via session flag, returns immediately.
 */
export async function probeStorageCapability(): Promise<StorageMode> {
  if (mode === "degraded") return "degraded";
  if (probePromise) return probePromise;

  probePromise = (async () => {
    try {
      await idbProbe();
      // Successful probe — stay ok unless something else already degraded us mid-flight.
      if (getStorageMode() !== "degraded") {
        mode = "ok";
        notify();
      }
      return getStorageMode();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      markStorageDegraded(`idb_probe: ${msg}`);
      return "degraded";
    }
  })();

  return probePromise;
}
